/**
 * @fileoverview Audit Log display helpers.
 * @description Small pure helpers the Audit Log table and detail dialog share: result badge
 * variants and label keys, readable names for the backend's SHOUTING_SNAKE event codes, localized
 * timestamps, and a safe text form of a change's before/after value (any JSON value).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { AuditActorType, AuditResult } from '@/features/audit/types/audit.types'

export const RESULT_VARIANT: Record<AuditResult, 'success' | 'danger' | 'warning'> = {
  SUCCESS: 'success',
  FAILED: 'danger',
  DENIED: 'warning',
}

export const RESULT_LABEL_KEY: Record<AuditResult, string> = {
  SUCCESS: 'audit.result.SUCCESS',
  FAILED: 'audit.result.FAILED',
  DENIED: 'audit.result.DENIED',
}

export const ACTOR_TYPE_LABEL_KEY: Record<AuditActorType, string> = {
  USER: 'audit.actorType.USER',
  SYSTEM: 'audit.actorType.SYSTEM',
  SERVICE: 'audit.actorType.SERVICE',
}

const PLACEHOLDER = '—'

/**
 * @description Turns a backend event code into readable text: USER_LOGIN_SUCCEEDED becomes "User
 * login succeeded". The code itself is still shown beside it, since the filter matches it exactly.
 * @param {string} code - An event type or category.
 * @returns {string} Sentence-cased text.
 */
export function humanizeCode(code: string): string {
  const words = code
    .replace(/[_.:-]+/g, ' ')
    .trim()
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * @description Formats an event's UTC instant in the viewer's time zone and language.
 * @param {string} iso - The ISO 8601 instant.
 * @param {string} locale - The active language tag (e.g. "en", "hi", "mr").
 * @returns {string} A date and time, or the raw value when it is not a valid instant.
 */
export function formatOccurredAt(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'medium' }).format(date)
}

/**
 * @description Turns a change's before or after value into text. Strings are shown as they are;
 * anything else as compact JSON.
 * @param {unknown} value - A JSON value, or undefined when the side was not recorded.
 * @returns {string} Text for display ("—" when there is no value).
 */
export function formatChangeValue(value: unknown): string {
  if (value === undefined || value === null) return PLACEHOLDER
  if (typeof value === 'string') return value === '' ? PLACEHOLDER : value
  return JSON.stringify(value)
}

/**
 * @description Joins a resource's type and id for one line ("USER_ROLE_ASSIGNMENT · assignment-500").
 * @param {{ resourceType: string; resourceId?: string } | undefined} resource - The resource.
 * @returns {string} The line, or "—" when the event names no resource.
 */
export function describeResource(
  resource: { resourceType: string; resourceId?: string } | undefined,
): string {
  if (!resource) return PLACEHOLDER
  return resource.resourceId
    ? `${resource.resourceType} · ${resource.resourceId}`
    : resource.resourceType
}
